// Bestseller Ticker - Scrolling feed of bestsellers from various platforms
import React, { useState, useEffect } from 'react';
import { TrendingUp, Award, Star } from 'lucide-react';
import { api } from '../utils/api';

export function BestsellerTicker() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [bestsellers, setBestsellers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch bestsellers from API
  useEffect(() => {
    const fetchBestsellers = async () => {
      try {
        const response = await api.request('/bestsellers/list');
        if (response && response.success) {
          // Map icon strings to actual icon components
          const iconMap = {
            'Award': Award,
            'Star': Star,
            'TrendingUp': TrendingUp
          };
          
          const dataWithIcons = response.data.map(item => ({
            ...item,
            icon: iconMap[item.icon] || TrendingUp
          }));
          
          setBestsellers(dataWithIcons);
        }
      } catch (error) {
        console.error('Error fetching bestsellers:', error);
        // Fallback to default data if API fails
        setBestsellers([
          { platform: 'NYT', title: 'Fourth Wing', author: 'Rebecca Yarros', category: 'Fiction', icon: Award },
          { platform: 'Amazon', title: 'The Heaven & Earth Grocery Store', author: 'James McBride', category: 'Fiction', icon: Star }
        ]);
      } finally {
        setLoading(false);
      }
    };

    fetchBestsellers();
  }, []);

  useEffect(() => {
    if (bestsellers.length === 0) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % bestsellers.length);
    }, 4000); // Change every 4 seconds

    return () => clearInterval(interval);
  }, [bestsellers.length]);

  const platformColors = {
    'NYT': 'from-amber-500 to-yellow-600',
    'Amazon': 'from-orange-500 to-amber-600',
    'Netflix': 'from-red-600 to-rose-700',
    'Paramount+': 'from-blue-600 to-indigo-700',
    'Hulu': 'from-green-500 to-emerald-600'
  };

  if (loading || bestsellers.length === 0) {
    return (
      <div className="w-full bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 py-6 overflow-hidden border-y-4 border-yellow-400">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-center gap-4">
            <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2">
              <TrendingUp className="h-5 w-5 text-yellow-400" />
              <span className="text-white font-bold text-sm">TRENDING NOW</span>
            </div>
            <span className="text-white text-sm">Loading bestsellers...</span>
          </div>
        </div>
      </div>
    );
  }

  const currentItem = bestsellers[currentIndex];
  const Icon = currentItem.icon;

  return (
    <div className="w-full bg-gradient-to-r from-purple-900 via-indigo-900 to-blue-900 py-6 overflow-hidden border-y-4 border-yellow-400">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-center gap-4">
          {/* Label */}
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-2 flex-shrink-0">
            <TrendingUp className="h-5 w-5 text-yellow-400" />
            <span className="text-white font-bold text-sm hidden sm:inline">TRENDING NOW</span>
          </div>

          {/* Animated Ticker Content */}
          <div className="flex-1 min-w-0">
            <div 
              className="transition-all duration-1000 ease-in-out"
              key={currentIndex}
            >
              <div className="flex items-center justify-center gap-3 text-white">
                {/* Platform Badge */}
                <div className={`bg-gradient-to-r ${platformColors[currentItem.platform]} px-3 py-1 rounded-full flex items-center gap-1 flex-shrink-0`}>
                  <Icon className="h-4 w-4" />
                  <span className="text-xs font-bold">{currentItem.platform}</span>
                </div>

                {/* Content */}
                <div className="flex items-center gap-2 min-w-0">
                  <span className="font-bold text-lg truncate">{currentItem.title}</span>
                  {currentItem.author && (
                    <>
                      <span className="text-purple-300 hidden sm:inline">by</span>
                      <span className="text-purple-200 hidden sm:inline truncate">{currentItem.author}</span>
                    </>
                  )}
                  {currentItem.type && (
                    <span className="text-purple-200 text-sm hidden md:inline">({currentItem.type})</span>
                  )}
                  <span className="text-xs bg-white/20 px-2 py-1 rounded hidden lg:inline">
                    {currentItem.category}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="hidden md:flex gap-1">
            {bestsellers.map((_, idx) => (
              <div
                key={idx}
                className={`h-1 w-8 rounded-full transition-all ${
                  idx === currentIndex ? 'bg-yellow-400' : 'bg-white/30'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Note about data freshness */}
        <div className="text-center mt-3">
          <p className="text-xs text-purple-200">
            📊 Live bestsellers from NYT, Amazon, Netflix & streaming platforms • Updates daily
          </p>
        </div>
      </div>
    </div>
  );
}
